import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Plus } from 'lucide-react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Textarea } from '../../ui/Textarea'
import { Switch } from '../../ui/switch'
import { planManagementApi, type CreatePlanRequest } from '../../../services/planManagementApi'

interface CreatePlanModalProps {
  onClose: () => void
}

interface FormData extends CreatePlanRequest {
  price_monthly_dollars: string  // For display purposes
  price_yearly_dollars: string   // For display purposes
  storage_gb: string            // For display purposes
}

export function CreatePlanModal({ onClose }: CreatePlanModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    price_monthly_dollars: '0.00',
    price_yearly_dollars: '0.00',
    messages_limit: 1000,
    credits_limit: 10000,
    team_members_limit: 1,
    chatbots_limit: 1,
    storage_limit_mb: 1000,
    teams_limit: 2,
    storage_gb: '1',
    features: {
      storage_gb_limit: 1,
      api_access: false,
      custom_branding: false,
      priority_support: false,
      advanced_analytics: false,
      white_label: false
    },
    is_active: true,
    sort_order: 0
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useMutation({
    mutationFn: planManagementApi.createPlan,
    onSuccess: () => {
      onClose()
    },
    onError: (error: any) => {
      console.error('Failed to create plan:', error)
      setErrors({ submit: error.response?.data?.detail || 'Failed to create plan' })
    }
  })

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear related errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleFeatureChange = (feature: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features!,
        [feature]: value
      }
    }))
  }

  const handleNumericFeatureChange = (feature: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features!,
        [feature]: value
      }
    }))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required'
    } else if (!/^[a-z_]+$/.test(formData.name)) {
      newErrors.name = 'Plan name must be lowercase letters and underscores only'
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required'
    }

    if (formData.messages_limit < -1) {
      newErrors.messages_limit = 'Messages limit must be -1 (unlimited) or positive'
    }

    if (formData.team_members_limit < -1) {
      newErrors.team_members_limit = 'Team members limit must be -1 (unlimited) or positive'
    }

    if (formData.chatbots_limit < -1) {
      newErrors.chatbots_limit = 'Chatbots limit must be -1 (unlimited) or positive'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    // Convert dollar amounts to cents
    const submitData: CreatePlanRequest = {
      ...formData,
      price_monthly: Math.round(parseFloat(formData.price_monthly_dollars || '0') * 100),
      price_yearly: formData.price_yearly_dollars ? Math.round(parseFloat(formData.price_yearly_dollars) * 100) : undefined,
      storage_limit_mb: Math.round(parseFloat(formData.storage_gb || '1') * 1024),
    }

    createMutation.mutate(submitData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Create Subscription Plan</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name *
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value.toLowerCase().replace(/[^a-z_]/g, ''))}
                  placeholder="e.g., professional"
                  className={errors.name ? 'border-red-300' : ''}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                <p className="mt-1 text-xs text-gray-500">Lowercase letters and underscores only</p>
              </div>

              <div>
                <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => handleChange('display_name', e.target.value)}
                  placeholder="e.g., Professional Plan"
                  className={errors.display_name ? 'border-red-300' : ''}
                />
                {errors.display_name && <p className="mt-1 text-sm text-red-600">{errors.display_name}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Plan description..."
                rows={3}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Pricing</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="price_monthly_dollars" className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Price ($)
                </label>
                <Input
                  id="price_monthly_dollars"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_monthly_dollars}
                  onChange={(e) => handleChange('price_monthly_dollars', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div>
                <label htmlFor="price_yearly_dollars" className="block text-sm font-medium text-gray-700 mb-1">
                  Yearly Price ($)
                </label>
                <Input
                  id="price_yearly_dollars"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_yearly_dollars}
                  onChange={(e) => handleChange('price_yearly_dollars', e.target.value)}
                  placeholder="0.00 (optional)"
                />
              </div>
            </div>
          </div>

          {/* Usage Limits */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Usage Limits</h3>
            <p className="text-sm text-gray-600">Use -1 for unlimited</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="messages_limit" className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Messages
                </label>
                <Input
                  id="messages_limit"
                  type="number"
                  min="-1"
                  value={formData.messages_limit}
                  onChange={(e) => handleChange('messages_limit', parseInt(e.target.value) || 0)}
                  className={errors.messages_limit ? 'border-red-300' : ''}
                />
                {errors.messages_limit && <p className="mt-1 text-sm text-red-600">{errors.messages_limit}</p>}
              </div>

              <div>
                <label htmlFor="credits_limit" className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly AI Credits
                </label>
                <Input
                  id="credits_limit"
                  type="number"
                  min="-1"
                  value={(formData as any).credits_limit}
                  onChange={(e) => handleChange('credits_limit' as any, parseInt(e.target.value) || -1)}
                />
                <p className="mt-1 text-xs text-gray-500">Cost-based limit. -1 for unlimited</p>
              </div>

              <div>
                <label htmlFor="teams_limit" className="block text-sm font-medium text-gray-700 mb-1">
                  Teams
                </label>
                <Input
                  id="teams_limit"
                  type="number"
                  min="-1"
                  value={(formData as any).features?.teams_limit ?? 2}
                  onChange={(e) => handleNumericFeatureChange('teams_limit', parseInt(e.target.value) || 0)}
                />
                <p className="mt-1 text-xs text-gray-500">Stored in features. Defaults: Free 1 • Starter 2 • Pro 10 • Business 25 • Enterprise Unlimited</p>
              </div>

              <div>
                <label htmlFor="team_members_limit" className="block text-sm font-medium text-gray-700 mb-1">
                  Team Members
                </label>
                <Input
                  id="team_members_limit"
                  type="number"
                  min="-1"
                  value={formData.team_members_limit}
                  onChange={(e) => handleChange('team_members_limit', parseInt(e.target.value) || 0)}
                  className={errors.team_members_limit ? 'border-red-300' : ''}
                />
                {errors.team_members_limit && <p className="mt-1 text-sm text-red-600">{errors.team_members_limit}</p>}
              </div>

              <div>
                <label htmlFor="chatbots_limit" className="block text-sm font-medium text-gray-700 mb-1">
                  Chatbots
                </label>
                <Input
                  id="chatbots_limit"
                  type="number"
                  min="-1"
                  value={formData.chatbots_limit}
                  onChange={(e) => handleChange('chatbots_limit', parseInt(e.target.value) || 0)}
                  className={errors.chatbots_limit ? 'border-red-300' : ''}
                />
                {errors.chatbots_limit && <p className="mt-1 text-sm text-red-600">{errors.chatbots_limit}</p>}
              </div>

              <div>
                <label htmlFor="teams_limit" className="block text-sm font-medium text-gray-700 mb-1">
                  Teams
                </label>
                <Input
                  id="teams_limit"
                  type="number"
                  min="-1"
                  value={formData.teams_limit}
                  onChange={(e) => handleChange('teams_limit', parseInt(e.target.value) || 0)}
                />
              </div>

              <div>
                <label htmlFor="storage_gb" className="block text-sm font-medium text-gray-700 mb-1">
                  Storage (GB)
                </label>
                <Input
                  id="storage_gb"
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.storage_gb}
                  onChange={(e) => handleChange('storage_gb', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Features</h3>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'api_access', label: 'API Access' },
                { key: 'custom_branding', label: 'Custom Branding' },
                { key: 'priority_support', label: 'Priority Support' },
                { key: 'advanced_analytics', label: 'Advanced Analytics' },
                { key: 'white_label', label: 'White Label' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">{label}</label>
                  <Switch
                    checked={formData.features?.[key] || false}
                    onCheckedChange={(checked) => handleFeatureChange(key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Plan Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Plan Settings</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <Input
                  id="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Active Plan</label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                />
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Plan
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
